// The offline copy, one constant per string. BL-2437: the label for a control the phone app disables
// offline (approved copy, operator, 2026-10-10). BL-2566: the Storage & Sync page and its Storage sheet.

/** The tooltip on a control that needs a connection. */
export const NOT_AVAILABLE_OFFLINE = 'Not available offline';

/** BUG-2462: offline, an auction page for an auction the device doesn't hold (under {@link NOT_AVAILABLE_OFFLINE}). */
export const NOT_SAVED_OFFLINE_LINE = "This auction isn't saved for offline use.";

/** The accessible name a sidebar control disabled offline carries beside its label (BL-2422, BUG-2071). */
export const NEEDS_CONNECTION = 'Needs a connection';

/** The page the menu's Offline section opens: the change queue and the device's storage (BL-2566). */
export const STORAGE_AND_SYNC_TITLE = 'Storage & Sync';

/** The offline bar's indicator while any change is queued (BL-2685). */
export const WAITING_TO_SYNC = 'Waiting to sync';

/** The page's status line while changes are queued and none failed (BL-2685). */
export const CHANGES_WAITING_LINE = 'Changes are waiting to sync';

/** The page's status line when the queue is empty (BL-2685). */
export const EVERYTHING_SYNCED_LINE = 'Everything is synced';

/** The sheet that lists the auctions saved on the device (BL-2566). */
export const STORAGE_TITLE = 'Storage';

/**
 * A saved auction's photo count while its photos download (BUG-2124, Michael Lawrie 2026-10-15),
 * on its Storage sheet card only: the page's status line stays silent (ADR 066 § 5.1).
 *
 * @param count - the thumbnails saved, of those the auction should have.
 * @returns e.g. "12,400 of 50,000 photos".
 */
export function photoCountLabel(count: { saved: number; total: number }): string {
  return `${count.saved.toLocaleString('en-US')} of ${count.total.toLocaleString('en-US')} photos`;
}

/**
 * The line under the Storage heading. A help-text exception to rule #44 (ADR 030 — Design Canon),
 * requested in these words by the operator (Michael Lawrie, 2026-10-14, BL-2566; ADR 066 — Offline
 * Mode § 8). It does not generalize.
 */
export const STORAGE_COPY =
  'The auctions below are saved for offline use. You can only edit lots for these auctions while offline. New auctions are added automatically.';

/**
 * The name of the switch that keeps an auction on this device (BL-2566, was "Sync offline"): the
 * Auctions page's column header (so the column picker and the phone card's Show all row), its
 * filter, and the switch's accessible name.
 */
export const SAVE_OFFLINE_LABEL = 'Save offline';

/*
 * BL-2565: the full-screen viewer offline (approved design, Michael Lawrie, 2026-10-14). The banners
 * and the queue note are rule #44 case 3 facts (ADR 030 — Design Canon): the label can't say why a
 * photo is small, why a video won't play, or that a change is held on the phone.
 */

/** The label on a photo shown from its saved preview. */
export const VIEWER_PREVIEW_LABEL = 'Preview';

/** The label on a video shown as its poster, because the file isn't on the phone. */
export const VIEWER_POSTER_LABEL = 'Poster only';

/** The label on a file picked offline, shown from the phone until it uploads. */
export const VIEWER_NOT_UPLOADED_LABEL = 'Not uploaded yet';

/** The banner over a photo's saved preview. */
export const VIEWER_PREVIEW_BANNER = "You're offline. This is the saved preview. Full size loads when you're back online.";

/** The banner over a video that can't play offline. */
export const VIEWER_VIDEO_BANNER = "You're offline. Videos aren't saved on this phone, so this one plays when you're back online.";

/*
 * Phone photo sheet: the viewer's gesture hint on a phone (rule #44 case 3: swipe and pinch can't be
 * read off the screen). A saved preview has nothing finer to zoom into, so its hint leaves zoom out.
 * (The offline queue note that sat under the actions here was dropped with the same design.)
 */

/** The hint under a photo that can page and zoom. */
export const VIEWER_SWIPE_ZOOM_HINT = 'Swipe for the next one · pinch to zoom';

/** The hint under a still that can page but not zoom (a saved preview, a staged file). */
export const VIEWER_SWIPE_HINT = 'Swipe for the next one';

/** The hint under the only photo: nothing to page to. */
export const VIEWER_ZOOM_HINT = 'Pinch to zoom';

/**
 * The reason the house switcher shows while offline (BUG-2469, Michael Lawrie 2026-11-02): a switch
 * needs the server to save it, so the other houses are locked in place.
 */
export const TENANT_SWITCH_OFFLINE = "Can't switch auction house while offline";
